import { useState, useContext, useEffect, useRef } from "react"
import { useHistory } from "react-router-dom"
import Pagination from "../../../../components/addon/pagination"
import tableFilter from "../../../../assets/images/table-filter.svg"
import AppContextObject from "../../../../common/context/common"
import config from "../../../../service/config"
import DataContext from "../../../../service/brandContext"
import filterSvg from "../../../../assets/images/filter-svg.svg"
import filterArrowSvg from "../../../../assets/images/filter-arrow-svg.svg"
import downloadSvg from "../../../../assets/images/download.svg"
import TopBar from "../../schools/components/topBar"
import BrandTopBar from "./brandTopBar"
import { returnReadableStatus, titleCase } from "../../../../service/helper"
import { WisrOptionService } from "../../../../service/wisrOptions"
import { DebounceInput } from 'react-debounce-input'
import searchSvg from "../../../../assets/images/search.svg"
import CustomSearchInput from "../../../../components/formFields/customSearchInput"
import BrandBackBtn from "./brandBackBtn"
import useClickOutside from "../../../../service/useClickOutside"
import helpImg from "../../../../assets/images/help.svg"

const Competition = () => {
    const filterRef = useRef(null)
    const filterOptionsRef = useRef(null)
    const history = useHistory()
    const [stateList, setStateList] = useState([])
    const [selectedState, setSelectedState] = useState([])
    const [cityList, setCityList] = useState([])
    const [searchInpuOpen, setSearchInpuOpen] = useState(false)
    const [clearSearchIcon, setClearSearchIcon] = useState(false)
    const parentData = useContext(DataContext)
    const context = useContext(AppContextObject)
    const [totalPage, setTotalPage] = useState([0])
    const [currentPage, setCurrentpage] = useState(1)
    const [dataLoaded, setDataLoaded] = useState(false)
    const [searchValue, setSearchValue] = useState("")
    const [filterByCity, setFilterByCity] = useState([])
    const [filterByLevel, setFilterByLevel] = useState([])
    const [filterByApproval, setFilterByApproval] = useState([])
    const [schoolList, setSchoolList] = useState([])
    const [showFilterModal, setShowFilterModal] = useState(false)
    const [showCityFilter, setShowCityFilter] = useState(false)
    const [showLevelFilter, setShowLevelFilter] = useState(false)
    const [showApprovalFilter, setShowApprovalFilter] = useState(false)
    const [showStateFilter, setShowStateFilter] = useState(false)
    const [dataPerPage] = useState(25)
    const [isPageLoading, setIsPageLoading] = useState(false)
    const [sortingData, setSortingData] = useState({_id: -1})
    const [fetchingSchoolForFirstTime, setFetchingSchoolForFirstTime] = useState(true)
    const [allSchoolsData, setAllSchoolsData] = useState([])
    const [totalCount, setTotalCount] = useState(0)
    const [schoolStats, setSchoolStats] = useState({ total: 0, approved: 0 })

    // Helper function to map category numbers to readable text
    const getCategoryText = (category) => {
        if (!category) return '-'
        
        // Handle numeric categories
        if (typeof category === 'number') {
            switch (category) {
                case 1: return 'A'
                case 2: return 'B'
                case 3: return 'C'
                default: return titleCase(category.toString())
            }
        }
        
        // Handle string categories
        if (typeof category === 'string') {
            const categoryLower = category.toLowerCase()
            
            // Map Category A, B, C to A, B, C
            if (categoryLower === 'a' || categoryLower === 'category a' || categoryLower === 'cat a') return 'A'
            if (categoryLower === 'b' || categoryLower === 'category b' || categoryLower === 'cat b') return 'B'
            if (categoryLower === 'c' || categoryLower === 'category c' || categoryLower === 'cat c') return 'C'
            
            // Keep existing numeric mappings for backward compatibility
            if (categoryLower === '1' || categoryLower === 'boys') return 'A'
            if (categoryLower === '2' || categoryLower === 'girls') return 'B'
            if (categoryLower === '3' || categoryLower === 'co-ed' || categoryLower === 'coed') return 'C'
            
            return titleCase(category)
        }
        
        return titleCase(category.toString())
    }

    // Helper function to get level text
    const getLevelText = (data) => {
        if (data.level) return titleCase(data.level)
        if (data.isLevel3) return 'Level 3'
        if (data.isLevel2) return 'Level 2'
        if (data.isLevel1) return 'Level 1'
        return '-'
    }

    // Helper function to get approval status with styling
    const getApprovalStatus = (data) => {
        const isApproved = data.isApproved === true || data.initialApproval === true
        return {
            text: isApproved ? 'Approved' : 'Pending',
            className: isApproved ? 'status-approved' : 'status-not-approved'
        }
    }

    // Helper function to build consistent query parameters for filtering
    const buildQueryArgs = () => {
        let queryArgs: any = {};
        
        // Add state filter - try different formats for backend compatibility
        if (selectedState.length > 0) {
            // Try multiple formats to ensure backend compatibility
            if (selectedState.length === 1) {
                queryArgs.state = selectedState[0];
                queryArgs.states = [selectedState[0]];
                queryArgs.stateName = selectedState[0]; // Alternative parameter name
            } else {
                queryArgs.state = selectedState;
                queryArgs.states = selectedState;
                queryArgs.stateNames = selectedState; // Alternative parameter name
            }
        }
        
        // Add city filter - match HTML file format exactly
        if (filterByCity.length > 0) {
            if (filterByCity.length === 1) {
                queryArgs.city = titleCase(filterByCity[0]); // Ensure proper case
            } else {
                queryArgs.city = filterByCity.map(city => titleCase(city)); // Ensure proper case for all cities
            }
        }
        
        // Add level filters - use individual boolean flags as backend expects
        if (filterByLevel.length > 0) {
            // For multiple levels, we need to send them in a way that backend understands as OR condition
            // Backend should return schools that match ANY of the selected levels
            filterByLevel.forEach(level => {
                if(level === 'level1') queryArgs.isLevel1 = true;
                if(level === 'level2') queryArgs.isLevel2 = true;
                if(level === 'level3') queryArgs.isLevel3 = true;
            });
            
            // If multiple levels are selected, also send as array for backend compatibility
            if (filterByLevel.length > 1) {
                queryArgs.selectedLevels = filterByLevel;
            }
        }
        
        // Add approval filter
        if (filterByApproval.length > 0) {
            // If both are checked, do not send approval filter (show all)
            if (!(filterByApproval.includes('approved') && filterByApproval.includes('non-approved'))) {
                if (filterByApproval.includes('approved')) queryArgs.initialApproval = true;
                if (filterByApproval.includes('non-approved')) queryArgs.initialApproval = false;
            } else {
                // Both approved and non-approved selected - show all
                // Don't send approval filter
            }
        }
        
        // Add search filter
        if (searchValue && searchValue.trim()) queryArgs.searchValue = searchValue.trim();
        
        return queryArgs;
    }

    useClickOutside(filterOptionsRef, (event) => {
        const el = filterRef?.current;
        if (!el || el.contains(event.target)) {
            return;
        }
        setShowFilterModal(false)
    })

    const onPageChange = (page) => {
        setCurrentpage(page)
    }
    
    const onNextClick = () => {
        setCurrentpage(prev => prev + 1)
    }
    
    const onPrevClick = () => {
        setCurrentpage(prev => prev - 1)
    }

    useEffect(() => {
        if(context && context.apis && context.apis.public) {
            getAllSchoolList()
        }
    }, [context])



    useEffect(() => {
        if(parentData && parentData.setShowSidemenu){
            parentData.setShowSidemenu()
        }
    }, [])

    useEffect(() => {
        fetchState()
    }, [])

    useEffect(() => {
        fetchCityByState()
    }, [selectedState])

    const fetchCityByState = async() => {
        if(selectedState.length > 0){
            const HP_BRAND_ID = "6888621e7800be0007711ff7";
            const ZEE_BRAND_ID = "695b91aacd5a37000777f98e";
            
            let resp;
            if (context.user.brand === HP_BRAND_ID && context.apis.public.getHpCitiesByState) {
                resp = await context.apis.public.getHpCitiesByState(
                context.validator, {states: selectedState}, {}
            )
            } else if (context.user.brand === ZEE_BRAND_ID && context.apis.public.getZeeCitiesByState) {
                resp = await context.apis.public.getZeeCitiesByState(
                    context.validator, {states: selectedState}, {}
                )
            } else {
                resp = await context.apis.public.getHpCitiesByState(
                    context.validator, {states: selectedState}, {}
                )
            }
        
            let city = []
            if(resp && resp.done){
                resp?.response?.rows?.forEach(row => {
                    if(row.cities && row.cities.length > 0){
                        row.cities.forEach(cityObj => {
                            if(cityObj.city && !city.find(data => data.toLowerCase() === cityObj.city.toLowerCase())){
                                city.push(cityObj.city)
                            }
                        })
                    }
                })
                city = city.sort((a, b) => {
                    return a.toLowerCase() > b.toLowerCase() ? 1 : -1
                })
        
                setCityList([...city])
            }
        }
    }

    const fetchState = async() => {
        const HP_BRAND_ID = "6888621e7800be0007711ff7";
        const ZEE_BRAND_ID = "695b91aacd5a37000777f98e";
        
        let resp;
        if (context.user.brand === HP_BRAND_ID && context.apis.public.getHpStatesByCountryRegion) {
            resp = await context.apis.public.getHpStatesByCountryRegion(
            context.validator, {}, {}
        )
        } else if (context.user.brand === ZEE_BRAND_ID && context.apis.public.getZeeStatesByCountryRegion) {
            resp = await context.apis.public.getZeeStatesByCountryRegion(
                context.validator, {}, {}
            )
        } else {
            resp = await context.apis.public.getHpStatesByCountryRegion(
                context.validator, {}, {}
            )
        }

        let state = []
        if(resp && resp.done){
            resp?.response?.rows?.forEach(row => {
                state = [...state, ...row.states]
            })
            state = state.sort((a, b) => {
                return a.toLowerCase() > b.toLowerCase() ? 1 : -1
            })
    
            setStateList([...state])
        }
    }



    useEffect(() => {
        if(currentPage !== 1){
            setCurrentpage(1)
        }
    }, [filterByLevel, filterByCity, filterByApproval, searchValue, selectedState])

    // Load initial stats when component mounts
    useEffect(() => {
        const loadInitialStats = async () => {
            const stats = await calculateSchoolStats();
            setSchoolStats(stats);
        };
        loadInitialStats();
    }, []);

    // Update stats when filters change
    useEffect(() => {
        const updateStats = async () => {
            const stats = await calculateSchoolStats();
            setSchoolStats(stats);
        };
        updateStats();
    }, [selectedState, filterByCity, filterByLevel, filterByApproval, searchValue, currentPage]);

    // Force stats update when school list changes
    useEffect(() => {
        if (schoolList.length >= 0) { // Changed from > 0 to >= 0 to handle empty lists
            const updateStats = async () => {
                const stats = await calculateSchoolStats();
                setSchoolStats(stats);
            };
            updateStats();
        }
    }, [schoolList]);

    // Debug useEffect to monitor school list changes
    useEffect(() => {

    }, [schoolList])

    // Trigger filtering when filters change
    useEffect(() => {
        // Always call getFilteredSchoolList - it handles both filtered and unfiltered cases
        getFilteredSchoolList()
    }, [selectedState, filterByCity, filterByLevel, filterByApproval, searchValue, currentPage])

    const calculateSchoolStats = async () => {
        try {
            // Build query parameters for backend stats API based on current filters
            const queryArgs = buildQueryArgs();
            
            const HP_BRAND_ID = "6888621e7800be0007711ff7";
            const ZEE_BRAND_ID = "695b91aacd5a37000777f98e";
            
            // Call backend stats API with current filters - use brand-specific API
            let statsResponse;
            if (context.user.brand === HP_BRAND_ID && context.apis.public.getWisrHpSchoolsStats) {
                statsResponse = await context.apis.public.getWisrHpSchoolsStats(
                context.validator, 
                queryArgs, 
                {}
            );
            } else if (context.user.brand === ZEE_BRAND_ID && context.apis.public.getWisrZeeSchoolsStats) {
                statsResponse = await context.apis.public.getWisrZeeSchoolsStats(
                    context.validator, 
                    queryArgs, 
                    {}
                );
            } else {
                statsResponse = await context.apis.public.getWisrHpSchoolsStats(
                    context.validator, 
                    queryArgs, 
                    {}
                );
            }
            
            if (statsResponse && statsResponse.done && statsResponse.response) {
                const stats = statsResponse.response;
                const result = {
                    total: stats.totalSchools || 0,
                    approved: stats.approvedSchools || 0
                };
                return result;
            } else {
                // Fallback to current calculation
                const totalSchools = schoolList.length;
                const approvedSchools = schoolList.filter(school => 
                    school.isApproved === true || school.initialApproval === true
                ).length;
                const result = {
                    total: totalSchools,
                    approved: approvedSchools
                };
                return result;
            }
        } catch (error) {
            // Fallback to current calculation
            const totalSchools = schoolList.length;
            const approvedSchools = schoolList.filter(school => 
                school.isApproved === true || school.initialApproval === true
            ).length;
            return {
                total: totalSchools,
                approved: approvedSchools
            };
        }
    }

    const getFilteredSchoolList = async() => {
        setIsPageLoading(true)
        
        // Build query parameters for backend filtering
        const queryArgs = buildQueryArgs();
        
        let options = {limit: dataPerPage, page: currentPage, withCount: true, sort: sortingData};
        let resp;
        const HP_BRAND_ID = "6888621e7800be0007711ff7";
        const ZEE_BRAND_ID = "695b91aacd5a37000777f98e";
        
        try {
            // Use brand-specific API based on brand ID
            if (context.user.brand === HP_BRAND_ID && context.apis.public.getAllWisrHpSchools) {
            resp = await context.apis.public.getAllWisrHpSchools(
                context.validator, queryArgs, options
            );
            } else if (context.user.brand === ZEE_BRAND_ID && context.apis.public.getAllWisrZeeSchools) {
                resp = await context.apis.public.getAllWisrZeeSchools(
                    context.validator, queryArgs, options
                );
            } else {
                resp = await context.apis.public.getAllSchools(
                    context.validator, queryArgs, options
                );
            }
        } catch (error) {
            resp = await context.apis.public.getAllSchools(
                context.validator, queryArgs, options
            );
        } finally {
            setIsPageLoading(false);
        }
        
        if(resp && resp.done){
            const schoolsList = resp.response.rows || [];
            setSchoolList(schoolsList);
            setAllSchoolsData(schoolsList); // for current page only
            const totalCountFromBackend = Number(resp.response.count) || 0;
            setTotalCount(totalCountFromBackend);
            const totalPages = Math.ceil(totalCountFromBackend / dataPerPage);
            let arr = [];
            for(let i=0; i<totalPages; i++){
                arr.push(i);
            }
            setTotalPage([...arr]);
            setIsPageLoading(false);
        }
        setIsPageLoading(false);
    }

    const handleAddRemoveFilter = (type, data) => {
        if(type === "state"){
            // For state, use multiple selection - toggle the selected state
            let array = [...selectedState]
            if(array.includes(data)){
                array.splice(array.indexOf(data), 1) // Remove if already selected
            } else {
                array.push(data) // Add to selection
            }
            setSelectedState([...array])
            // Clear city when state changes
            if(filterByCity.length !== 0){
                setFilterByCity([])
            }
        }
        else if(type === "city"){
            // For city, use multiple selection - toggle the selected city
            let array = [...filterByCity]
            if(array.includes(data)){
                array.splice(array.indexOf(data), 1) // Remove if already selected
            } else {
                array.push(data) // Add to selection
            }
            setFilterByCity([...array])
        }
        else if(type === "level"){
            // For level, use multiple selection - toggle the selected level
            let array = [...filterByLevel]
            if(array.includes(data)){
                array.splice(array.indexOf(data), 1) // Remove if already selected
            } else {
                array.push(data) // Add to selection
            }
            setFilterByLevel([...array])
        }
        else if(type === "approval"){
            // For approval, use multiple selection - toggle the selected approval status
            let array = [...filterByApproval]
            if(array.includes(data)){
                array.splice(array.indexOf(data), 1) // Remove if already selected
            } else {
                array.push(data) // Add to selection
            }
            setFilterByApproval([...array])
        }
    }

    const getAllSchoolList = async() => {
        setIsPageLoading(true)
        
        const HP_BRAND_ID = "6888621e7800be0007711ff7";
        const ZEE_BRAND_ID = "695b91aacd5a37000777f98e";
        
        let resp
        try {
            // Use brand-specific API based on brand ID
            if (context.user.brand === HP_BRAND_ID && context.apis.public.getAllWisrHpSchools) {
                resp = await context.apis.public.getAllWisrHpSchools(
                    context.validator, {}, {limit: dataPerPage, page: currentPage, withCount: true, sort: sortingData}
                )
            } else if (context.user.brand === ZEE_BRAND_ID && context.apis.public.getAllWisrZeeSchools) {
                resp = await context.apis.public.getAllWisrZeeSchools(
                    context.validator, {}, {limit: dataPerPage, page: currentPage, withCount: true, sort: sortingData}
                )
            } else {
                resp = await context.apis.public.getAllSchools(
                    context.validator, {}, {limit: dataPerPage, page: currentPage, withCount: true, sort: sortingData}
                )
            }
        } catch (error) {
            resp = await context.apis.public.getAllSchools(
                context.validator, {}, {limit: dataPerPage, page: currentPage, withCount: true, sort: sortingData}
            )
        }
        
        if(resp && resp.done){
            let inventoryName = [], eventName = [], classRoomList = [], schoolsList = [], inventoryList = [], eventList = [], OptimizedSchool = []
            
            // Check if this is brand schools data (HP or ZEE - different structure) or regular schools data
            const isBrandSchoolsData = resp.response.rows.length > 0 && resp.response.rows[0].hasOwnProperty('schoolName')
            
            if (isBrandSchoolsData) {
                // Handle HP schools data structure
                schoolsList = resp.response.rows.map(item => ({
                    ...item,
                    // Add default values for missing properties that the UI expects
                    classrooms: item.classrooms || [],
                    inventories: item.inventories || [],
                    activities: item.activities || [],
                    // Map HP schools fields to expected fields
                    name: item.schoolName,
                    address: item.address,
                    city: item.city,
                    state: item.state,
                    pincode: item.pincode,
                    totalStudents: item.totalStudents,
                    requiredStudents: item.requiredStudents,
                    participatedStudents: item.participatedStudents,
                    isLevel1: item.isLevel1,
                    isLevel2: item.isLevel2,
                    isLevel3: item.isLevel3,
                    initialApproval: item.initialApproval,
                    level1Date: item.level1Date,
                    level2Date: item.level2Date,
                    level3Date: item.level3Date,
                    level1StudentsCount: item.level1StudentsCount,
                    level2StudentsCount: item.level2StudentsCount,
                    level3StudentsCount: item.level3StudentsCount
                }))
            } else {
                // Handle regular schools data structure
                resp.response.rows.forEach(item => {
                    classRoomList = [...classRoomList, ...(item.classrooms || [])]
                    inventoryList = [...inventoryList, ...(item.inventories || [])]
                    eventList = [...eventList, ...(item.activities || [])]
                    schoolsList.push(item)
                })
                
                inventoryList.forEach(item => {
                    if(!inventoryName.includes(item.parentName)){
                        inventoryName.push(item.parentName)
                    }
                })
                eventList.forEach(item => {
                    if(!eventName.includes(item.name)){
                        eventName.push(item.name)
                    }
                })
                
                const WisrOption = new WisrOptionService({
                    TargetAudience: "co-ed",
                    EventsNames: [...eventName],
                    InventoriesNames: [...inventoryName],
                    budgetRatio: {CatA: 0, CatB: 0, CatC: 0},
                    campaignBudget: 0,
                    campaignDurationInDays: 365,
                    classroomData: classRoomList,
                    eventData: eventList,
                    inventoryData: inventoryList,
                    schoolData: schoolsList,
                    noOfDaysInYear: 365,
                    percentageDiscountInWISR: 0,
                    percentageIncreaseInReach: 0,
                    totalInternalCostPerSchool: 0,
                });
                WisrOption.$OptimizedSchool.subscribe((school) => {
                    OptimizedSchool = [...school]
                })
                schoolsList = resp.response.rows.map(item => {
                    let found = OptimizedSchool.find(data => data._id === item._id)
                    if(found){
                        item["totalImpressions"] = found.impressions
                    }
                    return item
                })
            }
            
            setSchoolList([...schoolsList])
            setFetchingSchoolForFirstTime(false)
            
            // Store the total count from backend response
            const totalCountFromBackend = Number(resp.response.count) || 0
            setTotalCount(totalCountFromBackend)
            
            // Calculate pagination based on backend total count
            const totalPages = Math.ceil(totalCountFromBackend / dataPerPage)
            let arr = []
            for(let i=0; i<totalPages; i++){
                arr.push(i)
            }
            setTotalPage([...arr])
            setIsPageLoading(false)
        }
        setIsPageLoading(false)
        setIsPageLoading(false)
    }

    const handleSearch = async (e) => {
        if(e === ""){
            setSearchValue("")
        }
        else{
            setSearchValue(e.target.value)
        }
    }



    const openFilter = () => {
        setShowLevelFilter(false)
        setShowCityFilter(false)
        setShowApprovalFilter(false)
        setShowFilterModal(!showFilterModal)
    }

    const showFilterDropDown = (type) => {
        if(type === "state"){
            setShowStateFilter(!showStateFilter)
            setShowLevelFilter(false)
            setShowApprovalFilter(false)
        }
        if(type === "city"){
            setShowLevelFilter(false)
            setShowApprovalFilter(false)
            setShowCityFilter(!showCityFilter)
        }
        else if(type === "level"){
            setShowStateFilter(false)
            setShowLevelFilter(!showLevelFilter)
            setShowApprovalFilter(false)
            setShowCityFilter(false)
        }
        else if(type === "approval"){
            setShowStateFilter(false)
            setShowLevelFilter(false)
            setShowApprovalFilter(!showApprovalFilter)
            setShowCityFilter(false)
        }
    }

    const clearAllFilter = () => {
        setFilterByLevel([])
        setFilterByApproval([])
        setFilterByCity([])
        setSelectedState([])
        setShowStateFilter(false)
        setShowFilterModal(false)
        setShowLevelFilter(false)
        setShowApprovalFilter(false)
        setShowCityFilter(false)
    }

    const handleDownload = async () => {
        try {
            // Build query parameters for backend filtering (same as getFilteredSchoolList)
            const queryArgs = buildQueryArgs();

            // Fetch all data for download (not just current page)
            let allSchoolsData = [];
            let page = 1;
            const limit = 1000; // Fetch 1000 records per request
            
            const HP_BRAND_ID = "6888621e7800be0007711ff7";
            const ZEE_BRAND_ID = "695b91aacd5a37000777f98e";
            
            while (true) {
                let options = { limit: limit, page: page, withCount: false, sort: sortingData };
                let resp;
                
                try {
                    // Use brand-specific API based on brand ID
                    if (context.user.brand === HP_BRAND_ID && context.apis.public.getAllWisrHpSchools) {
                    resp = await context.apis.public.getAllWisrHpSchools(
                        context.validator, queryArgs, options
                    );
                    } else if (context.user.brand === ZEE_BRAND_ID && context.apis.public.getAllWisrZeeSchools) {
                        resp = await context.apis.public.getAllWisrZeeSchools(
                            context.validator, queryArgs, options
                        );
                    } else if (context.apis.public.getAllWisrHpSchools) {
                        resp = await context.apis.public.getAllWisrHpSchools(
                            context.validator, queryArgs, options
                        );
                    } else {
                        resp = await context.apis.public.getAllSchools(
                            context.validator, queryArgs, options
                        );
                    }
                } catch (error) {
                    resp = await context.apis.public.getAllSchools(
                        context.validator, queryArgs, options
                    );
                }
                
                if (resp && resp.done && resp.response.rows && resp.response.rows.length > 0) {
                    allSchoolsData = [...allSchoolsData, ...resp.response.rows];
                    page++;
                    
                    // If we got less than the limit, we've reached the end
                    if (resp.response.rows.length < limit) {
                        break;
                    }
                } else {
                    break;
                }
            }

            // Check if this is HP/ZEE schools data or regular schools data
            const isBrandSchoolsData = allSchoolsData.length > 0 && allSchoolsData[0].hasOwnProperty('schoolName')
            const isZeeBrand = context.user.brand === ZEE_BRAND_ID
            
            let csvData = []
            
            if (isBrandSchoolsData) {
                // HP/ZEE Schools CSV format - match table structure exactly
                csvData = [
                    ['SR No', 'School Name', 'State', 'City', 'Pincode', 'Category', 'Approval Status', 'Level']
                ]
                
                allSchoolsData.forEach((data, index) => {
                    const approvalStatus = (data.isApproved === true || data.initialApproval === true) ? 'Approved' : 'Pending'
                    const level = data.isLevel3 ? 'Level 3' : data.isLevel2 ? 'Level 2' : data.isLevel1 ? 'Level 1' : 'Not Started'
                    const category = getCategoryText(data.category)
                    
                    const row = [
                        index + 1, // Sequential numbering for all data
                        (data.schoolName || data.name) ? titleCase(data.schoolName || data.name) : '',
                        data.state ? titleCase(data.state) : '-',
                        (data.parentCity || data.city) ? titleCase(data.parentCity || data.city) : '-',
                        data.pincode || '-',
                        category,
                        approvalStatus,
                        level
                    ]
                    csvData.push(row)
                })
            } else {
                // Regular Schools CSV format - match table structure exactly
                csvData = [
                    ['SR No', 'School Name', 'State', 'City', 'Pincode', 'Category', 'Approval Status', 'Level']
                ]
                
                allSchoolsData.forEach((data, index) => {
                    const approvalStatus = (data.isApproved === true || data.initialApproval === true) ? 'Approved' : 'Pending'
                    const level = data.isLevel3 ? 'Level 3' : data.isLevel2 ? 'Level 2' : data.isLevel1 ? 'Level 1' : 'Not Started'
                    const category = getCategoryText(data.category)
                    
                    const row = [
                        index + 1, // Sequential numbering for all data
                        (data.schoolName || data.name) ? titleCase(data.schoolName || data.name) : '',
                        data.state ? titleCase(data.state) : '-',
                        (data.parentCity || data.city) ? titleCase(data.parentCity || data.city) : '-',
                        data.pincode || '-',
                        category,
                        approvalStatus,
                        level
                    ]
                    csvData.push(row)
                })
            }
            
            // Convert to CSV string with proper escaping for all fields
            const csvString = csvData.map(row => 
                row.map(field => {
                    // Convert field to string and escape all fields
                    const fieldString = String(field);
                    // Replace any existing quotes with double quotes and wrap in quotes
                    const escapedField = fieldString.replace(/"/g, '""');
                    return `"${escapedField}"`;
                }).join(',')
            ).join('\n')
            
            // Create and download file with brand-specific filename
            const blob = new Blob([csvString], { type: 'text/csv' })
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            const brandName = isZeeBrand ? 'ZEE' : (context.user.brand === HP_BRAND_ID ? 'HP' : 'Smartchamps')
            a.download = `${brandName}-${new Date().toISOString().split('T')[0]}.csv`
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            window.URL.revokeObjectURL(url)
        } catch (error) {
            alert('Error downloading CSV file. Please try again.')
        }
    }


    
    return (
        <>
            <style>
                {`
                    .filter-actions-container {
                        display: flex;
                        gap: 10px;
                        align-items: center;
                    }
                    .download-btn {
                        background: transparent;
                        border: 1px solid #ddd;
                        border-radius: 4px;
                        padding: 8px 12px;
                        cursor: pointer;
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        color: inherit;
                        text-decoration: none;
                        transition: all 0.2s;
                        font-family: inherit;
                        height: 45px;
                        box-sizing: border-box;
                        line-height: 1;
                    }
                    .download-btn:hover {
                        background: #f8f9fa;
                        border-color: #007bff;
                    }
                    .download-btn img {
                        width: 16px;
                        height: 16px;
                    }
                    .download-btn h3 {
                        margin: 0;
                        font-size: 14px;
                        font-weight: 500;
                    }
                    .table-container {
                        margin-bottom: 10px;
                    }
                    .no-school-found {
                        text-align: center !important;
                        padding: 30px 20px !important;
                        color: #666 !important;
                        font-style: italic !important;
                        background: #f9f9f9 !important;
                        border-radius: 4px !important;
                        margin: 10px 0 !important;
                        width: 100% !important;
                        display: block !important;
                    }
                    .table td.no-school-found {
                        text-align: center !important;
                        padding: 30px 20px !important;
                        color: #666 !important;
                        font-style: italic !important;
                        background: #f9f9f9 !important;
                        border-radius: 4px !important;
                        margin: 10px 0 !important;
                    }
                    .table th:nth-child(1) { width: 7% !important; }
                    .table th:nth-child(2) { width: 22% !important; }
                    .table th:nth-child(3) { width: 12% !important; }
                    .table th:nth-child(4) { width: 12% !important; }
                    .table th:nth-child(5) { width: 10% !important; }
                    .table th:nth-child(6) { width: 10% !important; }
                    .table th:nth-child(7) { width: 12% !important; }
                    .table th:nth-child(8) { width: 15% !important; }
                    .table td:nth-child(1) { width: 7% !important; }
                    .table td:nth-child(2) { width: 22% !important; }
                    .table td:nth-child(3) { width: 12% !important; }
                    .table td:nth-child(4) { width: 12% !important; }
                    .table td:nth-child(5) { width: 10% !important; }
                    .table td:nth-child(6) { width: 10% !important; }
                    .table td:nth-child(7) { width: 12% !important; }
                    .table td:nth-child(8) { width: 15% !important; }
                    .status-approved {
                        color: rgb(49, 194, 149) !important;
                        background: rgb(230, 248, 242) !important;
                        font-size: 12px !important;
                        padding: 4px 9px !important;
                        border-radius: 4px !important;
                        width: auto !important;
                        font-family: 'nexaregular' !important;
                        text-transform: capitalize !important;
                        display: inline-block !important;
                        font-weight: normal !important;
                        letter-spacing: normal !important;
                        border: none !important;
                        min-width: auto !important;
                        text-align: left !important;
                    }
                    .status-not-approved {
                        background: #fff1e1 !important;
                        color: #fe6e00 !important;
                        font-size: 12px !important;
                        padding: 4px 9px !important;
                        border-radius: 4px !important;
                        width: auto !important;
                        font-family: 'nexaregular' !important;
                        text-transform: capitalize !important;
                        display: inline-block !important;
                        font-weight: normal !important;
                        letter-spacing: normal !important;
                        border: none !important;
                        min-width: auto !important;
                        text-align: left !important;
                    }
                `}
            </style>
            <div className="col-xl-12 col-lg-12 col-md-12 col-xs-12">
                <h2 className="htx2 clearfix">Campaign Analysis</h2>
            </div>
            <div className="col-xl-6 col-lg-6 col-md-12 col-xs-12 mt-20">
                <div className="box-design">
                    <div className="impressions-list">
                        <div className="impressions-list1">
                            <span>
                                <svg width="26" height="25" viewBox="0 0 26 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="#070707" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M2 17L12 22L22 17" stroke="#070707" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M2 12L12 17L22 12" stroke="#070707" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </span>
                            <h2 style={{fontSize: '24px', fontWeight: 'bold', color: '#070707'}}>{schoolStats.total}</h2>
                            <p style={{fontSize: '16px', color: '#8A8A8A', fontWeight: '500'}}>Total Schools</p>
                        </div>
                        <div className="impressions-list1">
                            <span>
                                <svg width="26" height="25" viewBox="0 0 26 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M9 12L11 14L15 10" stroke="#070707" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#070707" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </span>
                            <h2 style={{fontSize: '24px', fontWeight: 'bold', color: '#070707'}}>{schoolStats.approved}</h2>
                            <p style={{fontSize: '16px', color: '#8A8A8A', fontWeight: '500'}}>Approved Schools</p>
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-xl-12 col-lg-12 col-md-12 col-xs-12 mt-10">
                <div className="row">
                    <div className="col-xl-6 col-lg-3 col-md-3 col-xs-12 mb-10 pl">
                        <CustomSearchInput
                            searchInpuOpen={searchInpuOpen}
                            setSearchInpuOpen={setSearchInpuOpen}
                            clearSearchIcon={clearSearchIcon}
                            setClearSearchIcon={setClearSearchIcon}
                            searchKeys={searchValue}
                            handleChange={handleSearch}
                            palaceholder="Search"
                        />
                    </div>
                    <div className="col-xl-6 col-lg-9 col-md-9 col-xs-12 mb-10 pr">
                        <div className="fR filterbox">
                            <div className="filter-actions-container">
                                <a href="javascript:void(0);" className="filterDrop" onClick={openFilter}>
                                    <img src={filterSvg} />
                                    <h3>Filter</h3>
                                    <img src={filterArrowSvg} />
                                </a>
                                <button className="download-btn" onClick={handleDownload}>
                                    <img src={downloadSvg} />
                                    <h3>Download</h3>
                                </button>
                            </div>
                            <div className={`filterDropDown ${showFilterModal === true && "open"}`}>
                                <div className="filterDropHead" ref={filterRef}>
                                    <h2>Filter <a href="javascript:void(0);" className="viewwLink" onClick={clearAllFilter}>Clear All</a></h2>
                                </div>
                                <div className="filterDropBody" ref={filterOptionsRef}>
                                    <div className={`filterDropClose ${showStateFilter === true && "open"}`}>
                                        <h3><a href="javascript:void(0);" onClick={() => showFilterDropDown("state")}>{`State ${selectedState.length > 0 ? `(${selectedState.length})` : ""}`}  <img src={filterArrowSvg} /></a></h3>
                                        <div className="filtercheckbox">
                                            {stateList.map((item, index) => (
                                                <div className="checkbox fnone" key={index} >
                                                    <input type="checkbox" id={item} checked={selectedState.includes(item)} onClick={(e) => handleAddRemoveFilter("state", item)}/>
                                                    <label htmlFor={item}>{titleCase(item)}</label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className={`filterDropClose ${showCityFilter === true && selectedState.length !== 0 && "open"}`}>
                                        <h3><a href="javascript:void(0);" onClick={() => showFilterDropDown("city")}>{`City ${filterByCity.length > 0 ? `(${filterByCity.length})` : ""}`}  <img src={filterArrowSvg} /></a></h3>
                                        <div className="filtercheckbox">
                                            {cityList.map((item, index) => {
                                                const isDisabled = selectedState.includes(item);
                                                return (
                                                    <div className="checkbox fnone" key={index} >
                                                        <input 
                                                            type="checkbox" 
                                                            id={item} 
                                                            checked={filterByCity.includes(item)} 
                                                            disabled={isDisabled}
                                                            onChange={(e) => {
                                                                if (!isDisabled) {
                                                                    handleAddRemoveFilter("city", item);
                                                                }
                                                            }}
                                                            style={{ 
                                                                opacity: isDisabled ? 0.3 : 1, 
                                                                cursor: isDisabled ? 'not-allowed' : 'pointer',
                                                                pointerEvents: isDisabled ? 'none' : 'auto'
                                                            }}
                                                        />
                                                        <label 
                                                            htmlFor={item} 
                                                            style={{ 
                                                                opacity: isDisabled ? 0.3 : 1, 
                                                                cursor: isDisabled ? 'not-allowed' : 'pointer',
                                                                pointerEvents: isDisabled ? 'none' : 'auto',
                                                                color: isDisabled ? '#999' : 'inherit'
                                                            }}
                                                        >
                                                            {titleCase(item)}
                                                        </label>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    <div className={`filterDropClose ${showLevelFilter === true && "open"}`}>
                                        <h3><a href="javascript:void(0);" onClick={() => showFilterDropDown("level")}>{`Level ${filterByLevel.length > 0 ? `(${filterByLevel.length})` : ""}`}  <img src={filterArrowSvg} /></a></h3>
                                        <div className="filtercheckbox">
                                            <div className="checkbox fnone">
                                                <input type="checkbox" id="level1" checked={filterByLevel.includes("level1")} onClick={() => handleAddRemoveFilter("level", "level1")}/>
                                                <label htmlFor="level1">Level 1</label>
                                            </div>
                                            <div className="checkbox fnone">
                                                <input type="checkbox" id="level2" checked={filterByLevel.includes("level2")} onClick={() => handleAddRemoveFilter("level", "level2")}/>
                                                <label htmlFor="level2">Level 2</label>
                                            </div>
                                        </div>
                                    </div>
                                    <div className={`filterDropClose ${showApprovalFilter === true && "open"}`}>
                                        <h3><a href="javascript:void(0);" onClick={() => showFilterDropDown("approval")}>{`Approval Status ${filterByApproval.length > 0 ? `(${filterByApproval.length})` : ""}`}  <img src={filterArrowSvg} /></a></h3>
                                        <div className="filtercheckbox">
                                            <div className="checkbox fnone">
                                                <input type="checkbox" id="approved" checked={filterByApproval.includes("approved")} onClick={() => handleAddRemoveFilter("approval", "approved")}/>
                                                <label htmlFor="approved">Approved</label>
                                            </div>
                                            <div className="checkbox fnone">
                                                <input type="checkbox" id="non-approved" checked={filterByApproval.includes("non-approved")} onClick={() => handleAddRemoveFilter("approval", "non-approved")}/>
                                                <label htmlFor="non-approved">Pending</label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-12 col-lg-12 col-md-12 col-xs-12 mb-10 pl pr">
                        <div className="table-container mt-10 addAdmininput fillterTableHeight">
                            <table className="table going-campaign tableSpacingtd">
                                <thead>
                                    <tr>
                                        <th>SR No</th>
                                        <th>School Name</th>
                                        <th>State</th>
                                        <th>City</th>
                                        <th>Pincode</th>
                                        <th>Category</th>
                                        <th>Approval Status</th>
                                        <th>Level</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {!isPageLoading ?
                                        schoolList.length !== 0 ? schoolList.map((data: any, index) => {
                                            const approvalStatus = getApprovalStatus(data)
                                            return (
                                                <tr key={index}>
                                                    <td>{((currentPage - 1) * dataPerPage) + (index + 1)}</td>
                                                    <td>
                                                        <div className="schoolNameTable">
                                                            <h4>{(data.schoolName || data.name) && titleCase(data.schoolName || data.name)}</h4>
                                                        </div>
                                                    </td>
                                                    <td>{data.state ? titleCase(data.state) : '-'}</td>
                                                    <td>{(data.parentCity || data.city) ? titleCase(data.parentCity || data.city) : '-'}</td>
                                                    <td>{data.pincode || '-'}</td>
                                                    <td>{getCategoryText(data.category)}</td>
                                                    <td>
                                                        <span className={approvalStatus.className}>
                                                            {approvalStatus.text}
                                                        </span>
                                                    </td>
                                                    <td>{getLevelText(data)}</td>
                                                </tr>
                                            )

                                        })
                                        :
                                        <tr style={{display: 'table-row'}}>
                                            <td colSpan={8} style={{
                                                textAlign: 'center',
                                                padding: '30px 20px',
                                                color: '#666',
                                                fontStyle: 'italic',
                                                background: '#f9f9f9',
                                                borderRadius: '4px',
                                                margin: '10px 0',
                                                width: '100%',
                                                display: 'table-cell',
                                                verticalAlign: 'middle'
                                            }}>
                                                No School found
                                            </td>
                                        </tr>
                                        :
                                        [...Array(10)].map((el, index) => {
                                            return (
                                                <tr key={index}>
                                                    {
                                                        [...Array(8)].map((el, index) => {
                                                            return (
                                                                <td key={index}>
                                                                    <div className="content-loader-block"></div>
                                                                </td>
                                                            )
                                                        })
                                                    }
                                                </tr>
                                            )
                                        })
                                    }
                                </tbody>
                            </table> 
                        </div>
                        {totalPage.length > 1 &&
                            <Pagination
                                currentPage={currentPage}
                                totalPage={totalPage}
                                onPageChange={onPageChange}
                                onNextClick={onNextClick}
                                onPrevClick={onPrevClick}
                                loading={isPageLoading}
                            />
                        }
                    </div>
                </div>
            </div>
        </>
    )
}

export default Competition 